import {
  RichTextInputWrapper,
  RichTextInputWrapperProps,
} from '@framework/form';
import Text from '@tiptap/extension-text';
import { EditorContent, useEditor } from '@tiptap/react';
import * as React from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import { $SpaceCompact } from '../../../../ui/form/SpaceCompact';
import { IReference } from '../../references/IReference';
import {
  ComputeExpression,
  ComputeExpressionValue,
} from '../ComputeExpression';
import { computeExpressionToJsonContent } from './computeExpressionToJsonContent';
import { ComputeMathFunctionRichTextButton } from './menu/ComputeMathFunctionRichTextButton';
import { ComputeExpressionNode } from './nodes/ComputeExpressionNode';
import { ComputeExpressionReferenceNode } from './nodes/ComputeExpressionReferenceNode';
import { ComputeExpressionRootNode } from './nodes/ComputeExpressionRootNode';
import { computeExpressionReferenceSuggestion } from './reference-suggestions/computeExpressionReferenceSuggestion';

export interface IComputeExpressionMinimalBaseInputProps {
  disabled?: boolean;
  references?: IReference[];
  onBlur?: (event: FocusEvent) => void;
  value: ComputeExpression | null | undefined;
  onChange: (value: ComputeExpression | null | undefined) => void;
  /** Dimensioni del campo di input */
  size?: RichTextInputWrapperProps['size'];
}

export const ExpressionsEditorStyles = styled.div<{ width?: string }>`
  width: ${props => props.width ?? 'auto'};
  .rule-expression-editor {
    display: flex;
    min-width: 184px;
    width: auto;
    max-height: 100%;
    .rte-input-editor {
      /* border-top-left-radius: 0;
      border-bottom-left-radius: 0; */
      width: 100%;
    }
  }
  .ProseMirror {
    min-height: 0;

    /* font-size: 12px; */
    /* color: rgba(0, 0, 0, 0.88);
    font-size: 14px; */
    font-family: ${props => props.theme.fontFamily};
    &.rte-input-lg {
      padding-top: 16px;
    }

    .compute-expression-node {
      margin-top: 1px;
      /* font-size: 16px; */
    }

    span.compute-expression-reference {
      background-color: ${props => props.theme.colorPrimary};
      color: white;
      font-size: 14px;
      font-weight: bold;
      border-radius: 4px;
      padding: 2px 8px;
      line-height: 1.5;
    }

    // Display placeholder
    // TODO importare l'estenzione di tipTap per abilitare il placeholder
    p.is-editor-empty:first-child::before {
      color: #adb5bd;
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
    }
  }
`;

/**
 * JSON content per la gestione dell'editor per essere compatibile
 * con la `ComputeExpressionValue` registrata nell'elemento
 */
export interface ComputeExpressionJsonContent {
  type: 'root';
  content: [
    {
      type: 'node';
      attrs: {
        class: string;
      };
      content: ComputeExpressionValue;
    },
  ];
}

export function ComputeExpressionMinimalBaseInput(
  props: IComputeExpressionMinimalBaseInputProps,
) {
  const { references, value, onChange, onBlur } = props;

  // Trasformo l'Expression in un JSON content adatto a Tiptap
  const content = useMemo(() => computeExpressionToJsonContent(value), [value]);

  const suggestions = useMemo(
    () => computeExpressionReferenceSuggestion(references ?? []),
    [references],
  );

  const editor = useEditor({
    extensions: [
      ComputeExpressionRootNode,
      ComputeExpressionNode,
      ComputeExpressionReferenceNode.configure({
        suggestion: suggestions,
      }),
      Text,
    ],
    editable: props.disabled ? false : true,
    content,
    onBlur: ({ event }) => {
      onBlur?.(event);
    },
    onUpdate: ({ editor }) => {
      const content = editor.getJSON() as ComputeExpressionJsonContent;
      const text = editor.getText();
      // console.log('content', content, 'text', text);
      onChange({ raw: text, value: content.content[0].content });
    },
    injectCSS: false,
    editorProps: {
      handlePaste: event => {
        return true;
      },
      attributes: {
        class: 'rich-text-input-editor',
      },
    },
  });

  return (
    <ExpressionsEditorStyles width="100%">
      <$SpaceCompact size={props.size ?? 'middle'}>
        <RichTextInputWrapper
          size={props.size}
          rows={1}
          // maxHeight={32}
          className="rule-expression-editor"
        >
          <EditorContent
            editor={editor}
            placeholder="Digita @ per i suggerimenti"
          />
        </RichTextInputWrapper>
        <ComputeMathFunctionRichTextButton
          editor={editor}
          onlyIcon
          buttonProps={{ isActive: true, size: props.size, type: 'default' }}
        />
      </$SpaceCompact>
    </ExpressionsEditorStyles>
  );
}
